/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { BasicFunction } from '../../../../helpers/jsHelpers/types';
import type { ChannelMap, NETGEM_API_CHANNEL } from '../../../../libs/netgemLibrary/v8/types/Channel';
import type { NETGEM_API_V8_RIGHTS } from '../../../../libs/netgemLibrary/v8/types/Rights';

import Localizer from '../../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../../helpers/messenger';
import { isChannelPlaybackGranted } from '../../../../helpers/rights/playback';
import { FEATURE_SUBSCRIPTION } from '../../../../redux/appConf/constants';
import { PictoArrowDown, PictoArrowUp } from '../../../pictos/Element';
import Channel from './Channel';
import './ChannelZapper.css';

const CHANNEL = Object.freeze({
  // Channel change will be effective after this time (in milliseconds). This allows not to send multiple channel changes in a short time
  ChangeDebounceTimeout: 300,
  // If there are strictly fewer channels than this number, the channel list cannot be opened
  CountThreshold: 2,
  // Same value as in CSS
  ImageContainerHeight: 60,
  // Channel list will stay open for that long after the mouse cursor left (in milliseconds)
  ListTimeout: 500,
});

export type ChannelZapperHandle = {
  goToNextChannel: BasicFunction,
  goToPreviousChannel: BasicFunction,
};

type ChannelZapperPropType = {
  +channelId: ?string,
  +onChannelChangedCallback: (channel: NETGEM_API_CHANNEL) => void,
};

// eslint-disable-next-line max-lines-per-function
const ChannelZapper = ({ channelId, onChannelChangedCallback }: ChannelZapperPropType, ref: React.RefSetter<ChannelZapperHandle>): React.Node => {
  // Store
  const channels = useSelector<ChannelMap>((state) => state.appConfiguration.deviceChannels);
  const defaultRights = useSelector<?NETGEM_API_V8_RIGHTS>((state) => state.appConfiguration.rightsDefault);
  const isSubscriptionFeatureEnabled = useSelector<boolean>((state) => state.appConfiguration.features[FEATURE_SUBSCRIPTION]);
  const userRights = useSelector<Array<string> | null>((state) => state.appConfiguration.rightsUser);

  // Refs
  const channelChangeDebounceTimer = useRef<TimeoutID | null>(null);
  const channelIds = useRef<Array<string>>([]);
  const channelIndices = useRef<Map<string, number>>(new Map());
  const channelListTimer = useRef<TimeoutID | null>(null);
  const savedChannelIndex = useRef<number | null>(null);

  // State
  const [channelIndex, setChannelIndex] = useState<number>(-1);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Memo
  const channelsToRender = React.useMemo<Array<NETGEM_API_CHANNEL>>(() => {
    channelIds.current = [];
    channelIndices.current.clear();

    let i = 0;

    return Object.values(channels)
      .sort((a, b) => a.number - b.number)
      .filter((channel) => {
        if (channel.isHidden) return false;
        return isChannelPlaybackGranted(isSubscriptionFeatureEnabled, channel.info?.rights, defaultRights, userRights);
      })
      .map((channel) => {
        channelIds.current.push(channel.epgid);
        channelIndices.current.set(channel.epgid, i);
        i += 1;
        return channel;
      });
  }, [channels, defaultRights, isSubscriptionFeatureEnabled, userRights]);

  // Derived
  const isUIDisabled = channelsToRender.length < CHANNEL.CountThreshold;
  const translateY = channelIndex * CHANNEL.ImageContainerHeight;

  const resetChannelChangeDebounceTimer = useCallback(() => {
    if (channelChangeDebounceTimer.current) {
      clearTimeout(channelChangeDebounceTimer.current);
      channelChangeDebounceTimer.current = null;
    }
  }, []);

  const resetChannelListTimer = useCallback(() => {
    if (channelListTimer.current) {
      clearTimeout(channelListTimer.current);
      channelListTimer.current = null;
    }
  }, []);

  const notifyChannelChange = useCallback(
    (newChannelIndex: number) => {
      const newChannelId = channelIds.current[newChannelIndex];
      const { [newChannelId]: channel } = channels;

      if (channel) {
        onChannelChangedCallback(channel);
      }
    },
    [channels, onChannelChangedCallback],
  );

  const debounceChannelChange = useCallback(
    (newChannelIndex: number) => {
      resetChannelChangeDebounceTimer();
      channelChangeDebounceTimer.current = setTimeout(notifyChannelChange, CHANNEL.ChangeDebounceTimeout, newChannelIndex);
    },
    [notifyChannelChange, resetChannelChangeDebounceTimer],
  );

  const setChannelByIndex = useCallback(
    (newChannelIndex: number, skipNotification: boolean) => {
      setChannelIndex(newChannelIndex);
      if (!skipNotification) {
        debounceChannelChange(newChannelIndex);
      }
    },
    [debounceChannelChange],
  );

  const setChannelById = useCallback(
    (id: string, skipNotification: boolean) => {
      const index = channelIndices.current.get(id);
      if (typeof index === 'number') {
        setChannelByIndex(index, skipNotification);
      }
    },
    [setChannelByIndex],
  );

  const openChannelList = useCallback(() => {
    if (isUIDisabled) {
      return;
    }

    savedChannelIndex.current = channelIndex;
    setIsOpen(true);
  }, [channelIndex]);

  const closeChannelList = useCallback(
    (id?: string) => {
      const savedChannelId = typeof savedChannelIndex.current === 'number' ? channelIds.current[savedChannelIndex.current] : null;

      if (id && id !== savedChannelId) {
        setChannelById(id, false);
      } else if (typeof savedChannelIndex.current === 'number') {
        setChannelByIndex(savedChannelIndex.current, true);
      }

      savedChannelIndex.current = null;
      setIsOpen(false);
    },
    [setChannelById, setChannelByIndex],
  );

  const handleChannelOnClick = useCallback(
    (id: string) => {
      if (isOpen) {
        closeChannelList(id);
      } else {
        openChannelList();
      }
    },
    [closeChannelList, isOpen, openChannelList],
  );

  // Only contains a cleanup function
  useEffect(
    () => () => {
      resetChannelChangeDebounceTimer();
      resetChannelListTimer();
      Messenger.off(MessengerEvents.CLOSE_CHANNEL_LIST, closeChannelList);
    },
    [closeChannelList, resetChannelChangeDebounceTimer, resetChannelListTimer],
  );

  useEffect(() => {
    Messenger.on(MessengerEvents.CLOSE_CHANNEL_LIST, closeChannelList);

    if (channelId) {
      // A channel Id was passed to the component: use it to initialize
      setChannelById(channelId, true);
    } else {
      // No channel Id was passed to the component: select the first channel
      setChannelByIndex(0, true);
    }

    return () => {
      Messenger.off(MessengerEvents.CLOSE_CHANNEL_LIST, closeChannelList);
    };
  }, [channelId, channels, channelsToRender, closeChannelList, setChannelById, setChannelByIndex]);

  const handleArrowUpOnClick = useCallback(() => {
    if (isUIDisabled) {
      return;
    }

    setChannelByIndex((channelIndex + 1) % channelsToRender.length, isOpen);
  }, [channelIndex, channelsToRender, isOpen]);

  const handleArrowDownOnClick = useCallback(() => {
    if (isUIDisabled) {
      return;
    }

    setChannelByIndex((channelIndex + channelsToRender.length - 1) % channelsToRender.length, isOpen);
  }, [channelIndex, channelsToRender, isOpen]);

  const handleChannelZapperOnMouseEnter = useCallback(() => {
    resetChannelListTimer();
  }, [resetChannelListTimer]);

  const handleChannelZapperOnMouseLeave = useCallback(() => {
    resetChannelListTimer();

    if (!isOpen) {
      return;
    }

    channelListTimer.current = setTimeout(() => {
      channelListTimer.current = null;
      closeChannelList();
    }, CHANNEL.ListTimeout);
  }, [closeChannelList, isOpen, resetChannelListTimer]);

  const handleOnWheel = useCallback(
    (event: SyntheticWheelEvent<HTMLElement>) => {
      const { deltaY } = event;

      if (isUIDisabled) {
        return;
      }

      if (deltaY < 0) {
        // Go to next channel
        handleArrowUpOnClick();
      } else {
        // Go to previous channel
        handleArrowDownOnClick();
      }
    },
    [handleArrowDownOnClick, handleArrowUpOnClick],
  );

  useImperativeHandle(ref, (): ChannelZapperHandle => {
    return {
      goToNextChannel: handleArrowUpOnClick,
      goToPreviousChannel: handleArrowDownOnClick,
    };
  });

  return (
    <div className={clsx('channelZapper', isOpen && 'open')} onMouseEnter={handleChannelZapperOnMouseEnter} onMouseLeave={handleChannelZapperOnMouseLeave} onWheel={handleOnWheel}>
      <div className={clsx('channelListOpener', isUIDisabled && 'disabled')} onMouseEnter={openChannelList} />
      <div className='channelList'>
        {isOpen ? null : <PictoArrowUp aria-label={Localizer.localize('accessibility.player.next_channel')} className={clsx(isUIDisabled && 'disabled')} onClick={handleArrowUpOnClick} />}
        <div className='channelContainer'>
          <div className='channelSlider' style={{ transform: `translateY(${translateY}px)` }}>
            {channelsToRender.map(({ consolidated, epgid, name }) => (
              <Channel aria-hidden={!isOpen && epgid !== channelId} id={epgid} imageId={consolidated?.imageId} isUIDisabled={isUIDisabled} key={epgid} name={name} onClick={handleChannelOnClick} />
            ))}
          </div>
        </div>
        {isOpen ? null : <PictoArrowDown aria-label={Localizer.localize('accessibility.player.previous_channel')} className={clsx(isUIDisabled && 'disabled')} onClick={handleArrowDownOnClick} />}
      </div>
    </div>
  );
};

export default React.forwardRef<ChannelZapperPropType, ChannelZapperHandle>(ChannelZapper) as React.ComponentType<{
  ...ChannelZapperPropType,
  +ref?: React.RefSetter<ChannelZapperHandle> | { current: ChannelZapperHandle | null, ... } | null,
}>;
